import {
    AsyncPipe,
    isPlatformBrowser,
    LowerCasePipe,
    NgTemplateOutlet,
} from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    inject,
    OnDestroy,
    PLATFORM_ID,
} from "@angular/core";
import {
    RouterLink,
    RouterLinkActive,
} from "@angular/router";
import { IconsDirective } from "@Front-end/seed/modules/icons";
import { RegistrationAnalyticInitSources } from "@Front-end/seed/modules/registration-v2";
import { UserDirective } from "@Front-end/seed/modules/user";
import { PlatformBrowserService } from "@Front-end/seed/services";
import { NgLetDirective } from "@Front-end/ui/directives/ng-let";
import { ModalOpenType } from "@Front-end/ui/models/modal";
import {
    ButtonV3KindDirective,
    ButtonV3PaletteDirective,
    ButtonV3SizeDirective,
} from "@Front-end/ui/modules/button-v3";
import { IconComponent } from "@Front-end/ui/modules/icon";
import { LocalizeRouterModule } from "@gilsdav/ngx-translate-router";
import { TranslateModule } from "@ngx-translate/core";
import { environment } from "../../../../../environments/environment";
import { ChooseLangsDirective } from "../../../../components/choose-langs/directives/choose-langs.directive";
import {
    FeatureFlags,
    hasFeatureFlag,
} from "../../../../constants/feature-flags.constants";
import { LangObj } from "../../../../models/lang.model";
import { AccordionService } from "../../../../services/accordion.service";
import { MirrorService } from "../../../../services/mirror.service";
import { AppPopupsService } from "../../../../store/services/app-popups.service";
import { LivechatButtonDirective } from "../../../live-chat/livechat-button.directive";
import { PictureComponent } from "../../../shared/picture/picture.component";
import { navigationsList } from "./constants/navigations.constants";
import { SocialsList } from "./constants/socials.constants";
import { FooterSliderComponent } from "./footer-slider/footer-slider.component";

@Component({
    selector: "ls-footer",
    imports: [
        RouterLink,
        LocalizeRouterModule,
        TranslateModule,
        ButtonV3KindDirective,
        ButtonV3PaletteDirective,
        ButtonV3SizeDirective,
        IconComponent,
        ChooseLangsDirective,
        RouterLinkActive,
        AsyncPipe,
        NgLetDirective,
        UserDirective,
        NgTemplateOutlet,
        PictureComponent,
        LivechatButtonDirective,
        LowerCasePipe,
        IconsDirective,
        FooterSliderComponent,
    ],
    templateUrl: "./footer.component.html",
    styleUrl: "./footer.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements AfterViewInit, OnDestroy {
    public minioUrl: string = environment.minioUrl;
    public validationUrl: string = environment.validationUrl;
    public hasCaptcha = environment.features.recaptcha;

    public hasLuckyWin: boolean = hasFeatureFlag(FeatureFlags.LUCKY_WIN);
    public currentLanguage: LangObj | undefined;
    public navigationsList = navigationsList;
    public socials = SocialsList;
    public readonly platformBrowserService = inject(PlatformBrowserService);
    public isBrowser: boolean = this.platformBrowserService.isBrowser;

    public readonly uniqueProjectTranslation = environment.uniqueProjectTranslation;

    private resizeObserver: ResizeObserver | null = null;

    private readonly appPopupsService: AppPopupsService = inject(AppPopupsService);
    private readonly accordionService: AccordionService = inject(AccordionService);
    private readonly mirrorService: MirrorService = inject(MirrorService);
    private readonly platformId: object = inject(PLATFORM_ID);
    private readonly document: Document = inject(DOCUMENT);
    private readonly hostElement = inject(ElementRef) as ElementRef<HTMLElement>;

    public get isMirror(): boolean {
        return this.mirrorService.isMirror();
    }

    public get accordionState(): boolean[] {
        return this.navigationsList.map((unused, index) =>
            this.accordionService.getAccordionState(index),
        );
    }

    public ngAfterViewInit(): void {
        if (!this.isMirror && this.validationUrl && environment.domain === "PROD" && this.isBrowser) {
            // @ts-ignore
            // eslint-disable-next-line camelcase
            anj_a6e05798_c03f_4d36_8af8_5c70f55822ce.init();
        }

        this.getFooterHeight();

        if (isPlatformBrowser(this.platformId)) {
            this.setupResizeObserver();

            if (this.resizeObserver) {
                this.resizeObserver.observe(this.hostElement.nativeElement);
                this.updateFooterHeight();
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    public onLanguageChange(language: LangObj): void {
        this.currentLanguage = language;
    }

    public toggleAccordion(index: number): void {
        this.accordionService.toggleAccordion(index);
    }

    public openWheel(): void {
        this.appPopupsService.fireOpenLuckyWheel(ModalOpenType.MANUAL);
    }

    public openWheelRegistration(): void {
        this.appPopupsService.fireOpenRegistration({
            source: RegistrationAnalyticInitSources.LUCKY_WIN_ATTEMPT,
        }, ModalOpenType.MANUAL);
    }

    private setupResizeObserver(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.updateFooterHeight();
        });
    }

    private getFooterHeight(): void {
        this.document.documentElement.style.setProperty(
            "--ls-footer-height",
            `${this.hostElement.nativeElement.offsetHeight}px`,
        );
    }

    private updateFooterHeight(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.document.documentElement.style.setProperty(
            "--ls-footer-height-updated",
            `${this.hostElement.nativeElement.offsetHeight}px`,
        );
    }
}
